import React from "react";
import { Link } from "react-router-dom";
import logoSrc from "../../assets/voorent-logo.png";
import Icon from "./Icon";

export function Logo({ height = 26 }) {
  return (
    <span className="v-logo" style={{ "--logo-h": `${height}px` }}>
      <img
        className="v-logo__img"
        src={logoSrc}
        width={Math.round((527 / 118) * height)}
        height={height}
        alt="Voorent"
      />
    </span>
  );
}

// One button API for links, router links and real buttons.
export function Button({
  variant = "primary",
  size,
  block,
  to,
  href,
  arrow,
  icon,
  className = "",
  children,
  ...rest
}) {
  const classes = [
    "v-btn",
    `v-btn--${variant}`,
    size ? `v-btn--${size}` : "",
    block ? "v-btn--block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {icon ? <Icon name={icon} size={18} /> : null}
      {children}
      {arrow ? <Icon name="arrowRight" size={16} className="v-btn__arrow" /> : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  );
}

export function Badge({ tone = "neutral", dot, children, className = "" }) {
  return (
    <span className={`v-badge v-badge--${tone} ${dot ? "v-badge--dot" : ""} ${className}`}>
      {children}
    </span>
  );
}

export function Skeleton({ width, height, radius, className = "", style }) {
  return (
    <span
      aria-hidden="true"
      className={`v-skeleton ${className}`}
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

export function SectionHeader({ eyebrow, title, description, action, as: Tag = "h2", id }) {
  return (
    <div className="v-section-header">
      <div>
        {eyebrow ? <span className="v-eyebrow">{eyebrow}</span> : null}
        <Tag className="v-section-header__title" id={id}>
          {title}
        </Tag>
        {description ? <p className="v-section-header__desc">{description}</p> : null}
      </div>
      {action ? <div className="v-section-header__action">{action}</div> : null}
    </div>
  );
}

const STATE_ICONS = {
  empty: "search",
  error: "alert",
  offline: "offline",
  cart: "bag",
  unavailable: "box",
};

// Friendly empty / error states. Never surfaces raw error details.
export function EmptyState({ kind = "empty", title, description, actions, compact }) {
  return (
    <div className={`v-state ${compact ? "v-state--compact" : ""}`} role={kind === "error" || kind === "offline" ? "alert" : undefined}>
      <span className={`v-state__icon v-state__icon--${kind}`}>
        <Icon name={STATE_ICONS[kind] || "info"} size={26} />
      </span>
      <h2 className="v-state__title">{title}</h2>
      {description ? <p className="v-state__desc">{description}</p> : null}
      {actions ? <div className="v-state__actions">{actions}</div> : null}
    </div>
  );
}

// react-snap prerenders with API requests blocked; never bake error states into
// the static HTML — keep the loading placeholder until the browser re-renders.
export function isPrerender() {
  return typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");
}

export function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// Standard error state for failed API calls.
export function LoadError({ onRetry, compact }) {
  const offline = isOffline();
  return (
    <EmptyState
      compact={compact}
      kind={offline ? "offline" : "error"}
      title={offline ? "You're offline" : "We couldn't load this right now"}
      description={
        offline
          ? "Check your internet connection and try again."
          : "Something went wrong on our side. Please try again in a moment."
      }
      actions={
        onRetry ? (
          <Button variant="secondary" icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        ) : null
      }
    />
  );
}
